import { useEffect, useRef, useState, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { selectionTakes } from '../../lib/takes'

/** One block's selected words. */
export interface Passage {
  blockId: string
  excerpt: string
}

interface Props {
  /** Selections count only inside this element's `[data-take]` roots. */
  rootRef: RefObject<HTMLElement | null>
  /** The selected words: one part per block (a passage can run over several paragraphs). */
  onTake(parts: Passage[]): void
  onCopy(excerpt: string): void
  /** The block whose text is selected (null when none), so the page can light it. */
  onBlock?(blockId: string | null): void
}

interface Shown {
  /** The block the selection starts in (the page lights it). */
  blockId: string
  parts: Passage[]
  x: number
  y: number
  below: boolean
}

/** Room the toolbar needs above a selection before it flips below (header + toolbar). */
const ABOVE_CLEARANCE = 110

function rectOf(range: Range, fallback: Element): DOMRect {
  // jsdom (tests) has no Range geometry; real browsers do.
  const r = typeof range.getBoundingClientRect === 'function' ? range.getBoundingClientRect() : null
  return r && (r.width > 0 || r.height > 0) ? r : fallback.getBoundingClientRect()
}

function coarsePointer(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches
  } catch {
    return false
  }
}

/**
 * The small dark toolbar over selected words — in one block or a passage
 * running over several: Take (into your
 * thoughts) and Copy. It waits for the mouse to be released before showing,
 * sits below the selection on touch screens (where the system's own menu is
 * above), and lingers a moment after the selection collapses so a tap on it
 * on iOS — which clears the selection first — still lands.
 */
export function SelectionToolbar({ rootRef, onTake, onCopy, onBlock }: Props) {
  const [shown, setShown] = useState<Shown | null>(null)
  const barRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let dragging = false
    let hideTimer: ReturnType<typeof setTimeout> | undefined
    let raf = 0

    const compute = () => {
      raf = 0
      const root = rootRef.current
      const sel = window.getSelection()
      const parts = root && sel && sel.rangeCount > 0 && !sel.isCollapsed ? selectionTakes(sel.getRangeAt(0), root) : []
      const hit = parts[0]
      if (!hit || !sel) {
        clearTimeout(hideTimer)
        hideTimer = setTimeout(() => setShown(null), 250)
        return
      }
      clearTimeout(hideTimer)
      // Only the words selected inside blocks: the bar sits over them, not over a margin the drag started in.
      const rect = rectOf(sel.getRangeAt(0), hit.root)
      const below = coarsePointer() || rect.top < ABOVE_CLEARANCE
      const x = Math.min(Math.max(rect.left + rect.width / 2, 80), window.innerWidth - 80)
      setShown({ blockId: hit.blockId, parts: parts.map(({ blockId, excerpt }) => ({ blockId, excerpt })), x, y: below ? rect.bottom + 10 : rect.top - 10, below })
    }
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(compute)
    }
    const onSelectionChange = () => {
      if (!dragging) compute()
    }
    const onDown = (e: PointerEvent) => {
      if (barRef.current?.contains(e.target as Node)) return
      dragging = e.pointerType === 'mouse'
    }
    const onUp = () => {
      if (!dragging) return
      dragging = false
      setTimeout(compute, 0)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShown(null)
    }

    document.addEventListener('selectionchange', onSelectionChange)
    document.addEventListener('pointerdown', onDown)
    document.addEventListener('pointerup', onUp)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      clearTimeout(hideTimer)
      cancelAnimationFrame(raf)
      document.removeEventListener('selectionchange', onSelectionChange)
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [rootRef])

  const selectedBlock = shown?.blockId ?? null
  useEffect(() => onBlock?.(selectedBlock), [onBlock, selectedBlock])

  if (!shown) return null

  const done = () => {
    window.getSelection()?.removeAllRanges()
    setShown(null)
  }
  const item = 'whitespace-nowrap rounded-[5px] px-2.5 py-[5px] hover:bg-card/10'
  return createPortal(
    <div
      ref={barRef}
      role="toolbar"
      aria-label="Selected text"
      onMouseDown={(e) => e.preventDefault()}
      style={{ left: shown.x, top: shown.y, transform: `translate(-50%, ${shown.below ? '0' : '-100%'})` }}
      className="fixed z-40 flex gap-0.5 rounded-lg bg-foreground p-1 font-sans text-[13px] font-medium text-card shadow-[var(--shadow-chip)]"
    >
      <button
        type="button"
        onClick={() => {
          onTake(shown.parts)
          done()
        }}
        className={`${item} flex items-center gap-1.5 bg-card/15`}
      >
        <span aria-hidden className="size-2 rounded-full bg-accent/15" />
        Take
      </button>
      <button
        type="button"
        onClick={() => {
          onCopy(shown.parts.map((p) => p.excerpt).join('\n\n'))
          done()
        }}
        className={item}
      >
        Copy
      </button>
    </div>,
    document.body,
  )
}
